import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { showToast } from '@/components/Notifications/Toast';
import { API_BASE_URL } from '../api/config';
import { useProject } from '@/contexts/ProjectContext';

/** True for a 409 whose response came from axios (the raw fetch-shaped body the
 * global errorHandler emits: `{ success: false, error: <message>, code, ... }`).
 * Mirrors the ApiError.status===409 check in hooks/useComponents.ts / api/components.ts
 * — same optimistic-lock contract (causeEffect.service.ts updateCauseEffect), just
 * reached over axios here instead of a hand-rolled fetch wrapper.
 */
function isVersionConflict(error: unknown): error is import('axios').AxiosError {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

function conflictMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || fallback;
  }
  return fallback;
}

// Types matching backend schema
export interface CauseEffect {
  id: string;
  type: 'FAILURE_MODE' | 'ROOT_CAUSE';
  category: 'MAN' | 'MACHINE' | 'METHOD' | 'MATERIAL' | 'MEASUREMENT' | 'ENVIRONMENT';
  title?: string | null;
  description: string;
  potentialConsequences?: string | null;
  severity?: number;
  occurrence?: number;
  detection?: number;
  // FEA-040: Secondary detection for reactive detection (informational only)
  detectionReactive?: number | null;
  // FEA-014: S/O/D justification fields
  severityJustification?: string | null;
  occurrenceJustification?: string | null;
  detectionJustification?: string | null;
  rpn: number;
  mitigationStrategy?: string;
  contributingFactors?: string;
  // Optimistic-locking version (causeEffect.service.ts increments this on every
  // update). Read it from the fetched entity and echo it back as
  // `expectedVersion` on the next update so the backend can 409 a stale write
  // instead of silently overwriting a concurrent edit.
  version?: number;
  createdAt: string;
  updatedAt: string;
  // C3-FMEA honesty guard (mirrors backend causeEffect.service.ts): when a FAILURE_MODE has
  // scored FailureCauseLink rows, its O/D are a DERIVED worst-cause cache — the backend
  // rejects a direct PATCH of occurrence/detection in that state (see scoreWrite.ts header).
  // Raw Prisma relation name as serialized by GET /api/cause-effects (list) and
  // GET /api/cause-effects/:id (detail) — both include this relation, unrenamed.
  FailureCauseLink_FailureCauseLink_failureModeIdToCauseEffect?: Array<{
    id: string;
    occurrence?: number | null;
    detection?: number | null;
  }>;
  // The OTHER link direction: this row AS a root cause → the failure mode(s) it
  // contributes to. A ROOT_CAUSE row has no own S/O/D — its score is its failure
  // mode's severity × this link's occurrence/detection. Returned by the list +
  // detail endpoints (occurrence/detection/rpn are the link's own scalars; the
  // nested failure mode carries severity/rpn).
  FailureCauseLink_FailureCauseLink_rootCauseIdToCauseEffect?: Array<{
    id: string;
    occurrence?: number | null;
    detection?: number | null;
    rpn?: number | null;
    CauseEffect_FailureCauseLink_failureModeIdToCauseEffect?: {
      id: string;
      severity?: number | null;
      rpn?: number | null;
    } | null;
  }>;
}

export interface CreateCauseEffectInput {
  type: 'FAILURE_MODE' | 'ROOT_CAUSE';
  category: 'MAN' | 'MACHINE' | 'METHOD' | 'MATERIAL' | 'MEASUREMENT' | 'ENVIRONMENT';
  title?: string | null;
  description: string;
  potentialConsequences?: string | null;
  severity?: number;
  occurrence?: number;
  detection?: number;
  // FEA-040: Secondary detection for reactive detection
  detectionReactive?: number | null;
  severityJustification?: string | null;
  occurrenceJustification?: string | null;
  detectionJustification?: string | null;
  mitigationStrategy?: string;
  contributingFactors?: string;
}

export interface UpdateCauseEffectInput {
  type?: 'FAILURE_MODE' | 'ROOT_CAUSE';
  category?: 'MAN' | 'MACHINE' | 'METHOD' | 'MATERIAL' | 'MEASUREMENT' | 'ENVIRONMENT';
  title?: string | null;
  description?: string;
  potentialConsequences?: string | null;
  severity?: number;
  occurrence?: number;
  detection?: number;
  // FEA-040: Secondary detection for reactive detection
  detectionReactive?: number | null;
  severityJustification?: string | null;
  occurrenceJustification?: string | null;
  detectionJustification?: string | null;
  mitigationStrategy?: string;
  contributingFactors?: string;
  // Optimistic locking: the version of the entity this edit was loaded from.
  // When supplied, the backend rejects the update with 409 if the row has
  // since changed (see causeEffect.service.ts updateCauseEffect).
  expectedVersion?: number;
}

export interface CauseEffectFilters {
  type?: 'FAILURE_MODE' | 'ROOT_CAUSE';
  category?: 'MAN' | 'MACHINE' | 'METHOD' | 'MATERIAL' | 'MEASUREMENT' | 'ENVIRONMENT';
  minRpn?: number;
  maxRpn?: number;
}

export interface CategoryStats {
  category: string;
  count: number;
  averageRPN: number;
  maxRPN: number;
  failureModes: CauseEffect[];
}

const API_BASE = `${API_BASE_URL}/cause-effects`;

// Query keys
const QUERY_KEYS = {
  all: ['cause-effects'] as const,
  lists: () => [...QUERY_KEYS.all, 'list'] as const,
  list: (filters: CauseEffectFilters) => [...QUERY_KEYS.lists(), filters] as const,
  details: () => [...QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...QUERY_KEYS.details(), id] as const,
  critical: () => [...QUERY_KEYS.all, 'critical'] as const,
  byCategory: () => [...QUERY_KEYS.all, 'by-category'] as const,
};

// Fetch all cause-effects with optional filters
export const useCauseEffects = (filters?: CauseEffectFilters) => {
  const { activeProject } = useProject();

  return useQuery<CauseEffect[], Error>({
    queryKey: ['cause-effects', activeProject?.id, filters],
    queryFn: async () => {
      if (!activeProject) return [];

      const params = new URLSearchParams();
      params.append('projectId', activeProject.id);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.minRpn !== undefined) params.append('minRpn', filters.minRpn.toString());
      if (filters?.maxRpn !== undefined) params.append('maxRpn', filters.maxRpn.toString());

      const response = await axios.get(`${API_BASE}?${params.toString()}`);
      return response.data.data || [];
    },
    enabled: !!activeProject,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
  });
};

// Fetch single cause-effect by ID
export const useCauseEffectById = (id: string) => {
  return useQuery<CauseEffect, Error>({
    queryKey: QUERY_KEYS.detail(id),
    queryFn: async () => {
      const response = await axios.get(`${API_BASE}/${id}`);
      return response.data.data || response.data;
    },
    enabled: !!id,
    staleTime: 30000,
  });
};

// Fetch critical failure modes (RPN > 200)
export const useCriticalFailureModes = () => {
  const { activeProject } = useProject();

  return useQuery<CauseEffect[], Error>({
    queryKey: ['cause-effects-critical', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];

      const response = await axios.get(`${API_BASE}/critical?projectId=${activeProject.id}`);
      return response.data.data || [];
    },
    enabled: !!activeProject,
    staleTime: 30000,
  });
};

// Fetch failures grouped by category
export const useFailuresByCategory = () => {
  const { activeProject } = useProject();

  return useQuery<CategoryStats[], Error>({
    queryKey: ['cause-effects-by-category', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];

      const response = await axios.get(`${API_BASE}/by-category?projectId=${activeProject.id}`);
      return response.data.data || [];
    },
    enabled: !!activeProject,
    staleTime: 60000, // 1 minute
  });
};

// ERROR-CE-001 FIX: Add validation to RPN calculation
function calculateRPN(severity?: number, occurrence?: number, detection?: number): number {
  // Validate inputs: must be numbers between 1-10
  const s = severity && severity >= 1 && severity <= 10 ? severity : 1;
  const o = occurrence && occurrence >= 1 && occurrence <= 10 ? occurrence : 1;
  const d = detection && detection >= 1 && detection <= 10 ? detection : 1;

  return s * o * d;
}

// Create new cause-effect
export const useCreateCauseEffect = () => {
  const queryClient = useQueryClient();
  const { activeProject } = useProject();

  return useMutation<CauseEffect, Error, CreateCauseEffectInput>({
    mutationFn: async (input) => {
      if (!activeProject) throw new Error('No active project');
      const response = await axios.post(`${API_BASE}?projectId=${activeProject.id}`, input);
      return response.data.data || response.data;
    },
    onMutate: async (newCauseEffect) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.lists() });

      // Snapshot previous value
      const previousCauseEffects = queryClient.getQueryData<CauseEffect[]>(
        QUERY_KEYS.list({})
      );

      // Optimistically update
      if (previousCauseEffects) {
        // Use validated RPN calculation
        const rpn = calculateRPN(
          newCauseEffect.severity,
          newCauseEffect.occurrence,
          newCauseEffect.detection
        );

        const optimisticCauseEffect: CauseEffect = {
          ...newCauseEffect,
          id: `temp-${Date.now()}`,
          rpn: rpn,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData<CauseEffect[]>(
          QUERY_KEYS.list({}),
          [...previousCauseEffects, optimisticCauseEffect]
        );
      }

      return { previousCauseEffects };
    },
    onError: (err, newCauseEffect, context) => {
      // BUG-CE-002 FIX: Ensure onError properly removes temp items
      const ctx = context as { previousCauseEffects?: CauseEffect[] } | undefined;
      if (ctx?.previousCauseEffects) {
        queryClient.setQueryData(QUERY_KEYS.list({}), ctx.previousCauseEffects);
      }
      showToast.error('Failed to create failure mode');
    },
    onSuccess: () => {
      showToast.success('Failure mode created successfully');
    },
    onSettled: () => {
      // Refetch after mutation - invalidate all cause-effects queries
      queryClient.invalidateQueries({ queryKey: ['cause-effects'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-critical'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-by-category'] });
    },
  });
};

// Update existing cause-effect
export const useUpdateCauseEffect = () => {
  const queryClient = useQueryClient();

  return useMutation<CauseEffect, Error, { id: string; input: UpdateCauseEffectInput }>({
    mutationFn: async ({ id, input }) => {
      const response = await axios.patch(`${API_BASE}/${id}`, input);
      return response.data.data || response.data;
    },
    onMutate: async ({ id, input }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.lists() });
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.detail(id) });

      // Snapshot previous values
      const previousCauseEffects = queryClient.getQueryData<CauseEffect[]>(
        QUERY_KEYS.list({})
      );
      const previousCauseEffect = queryClient.getQueryData<CauseEffect>(
        QUERY_KEYS.detail(id)
      );

      // Optimistically update list
      if (previousCauseEffects) {
        queryClient.setQueryData<CauseEffect[]>(
          QUERY_KEYS.list({}),
          previousCauseEffects.map((item) => {
            if (item.id === id) {
              const updated = { ...item, ...input, updatedAt: new Date().toISOString() };
              // Recalculate RPN if severity, occurrence, or detection changed (with validation)
              if (
                input.severity !== undefined ||
                input.occurrence !== undefined ||
                input.detection !== undefined
              ) {
                updated.rpn = calculateRPN(
                  input.severity ?? item.severity,
                  input.occurrence ?? item.occurrence,
                  input.detection ?? item.detection
                );
              }
              return updated;
            }
            return item;
          })
        );
      }

      return { previousCauseEffects, previousCauseEffect };
    },
    onError: (err, { id }, context) => {
      // BUG-CE-002 FIX: Proper rollback on error
      const ctx = context as
        | { previousCauseEffects?: CauseEffect[]; previousCauseEffect?: CauseEffect }
        | undefined;
      if (ctx?.previousCauseEffects) {
        queryClient.setQueryData(QUERY_KEYS.list({}), ctx.previousCauseEffects);
      }
      if (ctx?.previousCauseEffect) {
        queryClient.setQueryData(QUERY_KEYS.detail(id), ctx.previousCauseEffect);
      }
      if (isVersionConflict(err)) {
        // Optimistic-lock conflict (VERSION_CONFLICT) — someone else saved over
        // this failure mode/root cause. Don't leave a stale cache around;
        // resync from the server and tell the user plainly what happened so
        // they can review the other person's change before re-applying theirs
        // (mirrors hooks/useComponents.ts's 409 handling).
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
        showToast.error(
          conflictMessage(
            err,
            'This was changed by someone else — reload to see their changes before saving.'
          )
        );
        return;
      }
      showToast.error('Failed to update failure mode');
    },
    onSuccess: () => {
      showToast.success('Failure mode updated successfully');
    },
    onSettled: () => {
      // Refetch after mutation - invalidate all cause-effects queries
      queryClient.invalidateQueries({ queryKey: ['cause-effects'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-critical'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-by-category'] });
    },
  });
};

// Delete cause-effect
export const useDeleteCauseEffect = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await axios.delete(`${API_BASE}/${id}`);
    },
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.lists() });

      // Snapshot previous value
      const previousCauseEffects = queryClient.getQueryData<CauseEffect[]>(
        QUERY_KEYS.list({})
      );

      // Optimistically update
      if (previousCauseEffects) {
        queryClient.setQueryData<CauseEffect[]>(
          QUERY_KEYS.list({}),
          previousCauseEffects.filter((item) => item.id !== id)
        );
      }

      return { previousCauseEffects };
    },
    onError: (err, id, context) => {
      // BUG-CE-002 FIX: Proper rollback on error
      const ctx = context as { previousCauseEffects?: CauseEffect[] } | undefined;
      if (ctx?.previousCauseEffects) {
        queryClient.setQueryData(QUERY_KEYS.list({}), ctx.previousCauseEffects);
      }
      showToast.error('Failed to delete failure mode');
    },
    onSettled: () => {
      // Refetch after mutation - invalidate all cause-effects queries
      queryClient.invalidateQueries({ queryKey: ['cause-effects'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-critical'] });
      queryClient.invalidateQueries({ queryKey: ['cause-effects-by-category'] });
    },
  });
};
